import { api, request, requireAccount, withAccount } from "./core";

// Organization memberships (for connect-org flow). User-scoped, not
// galaxy-scoped — keys off the JWT's user_id. Safe without account_login.
export interface OrgMembership {
  login: string;
  avatar_url: string;
  role: string;    // "admin" or "member"
  type: string;    // "personal" or "org" — authoritative galaxy type
  connected: boolean;
}

export const orgApi = {
  // refresh=true bypasses the backend's per-user GitHub cache (60s TTL) so a
  // freshly-connected org surfaces immediately — used on the connect screen's
  // post-install refetch. Default (no refresh) serves the fast cached path.
  memberships: (refresh = false) =>
    api.get<OrgMembership[]>(refresh ? "/orgs/memberships?refresh=true" : "/orgs/memberships"),
};

export const accountApi = {
  delete: (accountLogin: string, includeSignals = false) =>
    request<{ status: string }>(
      withAccount("/account", requireAccount(accountLogin, "accountApi.delete"), includeSignals ? { include_signals: "true" } : undefined),
      { method: "DELETE" },
    ),
};

// Upload Tokens (CLI API Keys)
export interface UploadToken {
  id: number;
  account_id: number;
  user_id: number;
  name: string;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface CreateTokenResponse {
  id: number;
  name: string;
  token: string; // plaintext — shown only once
}

export const tokenApi = {
  list: (accountLogin: string) =>
    api.get<UploadToken[]>(withAccount("/tokens", requireAccount(accountLogin, "tokenApi.list"))),
  create: (accountLogin: string, name: string, expiresInDays?: number) =>
    api.post<CreateTokenResponse>(withAccount("/tokens", requireAccount(accountLogin, "tokenApi.create")), {
      name,
      expires_in_days: expiresInDays || 0,
    }),
  delete: (accountLogin: string, id: number) =>
    request<void>(withAccount("/tokens", requireAccount(accountLogin, "tokenApi.delete")), { method: "DELETE", body: JSON.stringify({ id }) }),
};

// MCP Tokens (mcp_ dedicated bearer tokens for the OrbitLens MCP context
// server — the AI-agent-facing read surface, distinct from the eis_ upload
// tokens above). Same shape/lifecycle as UploadToken, but a SEPARATE token
// family: an mcp_ token only grants read-only MCP tool access, never the
// eis_ upload-write capability.
export interface McpToken {
  id: number;
  account_id: number;
  user_id: number;
  name: string;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface CreateMcpTokenResponse {
  id: number;
  name: string;
  token: string; // plaintext, prefixed mcp_ — shown only once
}

export const mcpTokenApi = {
  list: (accountLogin: string) =>
    api.get<McpToken[]>(withAccount("/mcp-tokens", requireAccount(accountLogin, "mcpTokenApi.list"))),
  create: (accountLogin: string, name: string, expiresInDays?: number) =>
    api.post<CreateMcpTokenResponse>(withAccount("/mcp-tokens", requireAccount(accountLogin, "mcpTokenApi.create")), {
      name,
      expires_in_days: expiresInDays || 0,
    }),
  delete: (accountLogin: string, id: number) =>
    request<void>(withAccount("/mcp-tokens", requireAccount(accountLogin, "mcpTokenApi.delete")), { method: "DELETE", body: JSON.stringify({ id }) }),
};

// MCP usage — this galaxy's current hourly call count against its plan's cap.
// Reads the SAME Redis window + cap table the mcp-server's rate-limit
// middleware enforces (see MCPTokenHandler.GetMCPUsage on the backend) — a
// read-only mirror of the live gate for the FE's usage bar, never a second
// source of truth for the caps.
export interface McpUsage {
  plan: string;
  limit_per_hour: number;
  used_this_hour: number;
  window_resets_at: string;
}

export const mcpUsageApi = {
  get: (accountLogin: string) =>
    api.get<McpUsage>(withAccount("/mcp-usage", requireAccount(accountLogin, "mcpUsageApi.get"))),
};

// Onboarding
export interface OnboardingStatus {
  onboarding_completed: boolean;
  visibility_mode: string;
  // alumni_legacy_view: admin consent for departed members to watch their OWN
  // survival keep decaying in this galaxy (default false). Display-only consent —
  // never affects the Gravity Certificate (always frozen-at-departure).
  alumni_legacy_view: boolean;
  // open_share_enabled: admin consent that ANY signed-in user may self-add this
  // galaxy to their read-only shared list by typing its login (default false).
  open_share_enabled: boolean;
  // open_share_disclosure: how members appear to a self-add (open-share) viewer,
  // 'anonymous' (default) or 'full'. The pull-side twin of the per-invite choice.
  open_share_disclosure: string;
  // locale: the galaxy's display language ('ja'|'en'), driving channel-wide
  // localized output like the Slack digest. Galaxy-scoped, admin-set.
  locale: string;
  role: string;
}

export const onboardingApi = {
  status: (accountLogin: string) =>
    api.get<OnboardingStatus>(withAccount("/onboarding/status", requireAccount(accountLogin, "onboardingApi.status"))),
  complete: (accountLogin: string, visibilityMode: string) =>
    api.post<{ status: string }>(withAccount("/onboarding/complete", requireAccount(accountLogin, "onboardingApi.complete")), { visibility_mode: visibilityMode }),
  updateVisibility: (accountLogin: string, visibilityMode: string) =>
    api.put<{ status: string }>(withAccount("/onboarding/visibility", requireAccount(accountLogin, "onboardingApi.updateVisibility")), { visibility_mode: visibilityMode }),
  updateAlumniLegacyView: (accountLogin: string, enabled: boolean) =>
    api.put<{ alumni_legacy_view: boolean }>(withAccount("/onboarding/alumni-legacy-view", requireAccount(accountLogin, "onboardingApi.updateAlumniLegacyView")), { enabled }),
  // updateOpenShare toggles whether any signed-in user may self-add this galaxy to
  // their read-only shared list by login (admin-only). Widens who may start a share.
  updateOpenShare: (accountLogin: string, enabled: boolean) =>
    api.put<{ open_share_enabled: boolean }>(withAccount("/onboarding/open-share", requireAccount(accountLogin, "onboardingApi.updateOpenShare")), { enabled }),
  // updateOpenShareDisclosure sets how members appear to a self-add viewer
  // ('anonymous'|'full'; admin-only) — the pull-side twin of the per-invite choice.
  updateOpenShareDisclosure: (accountLogin: string, disclosure: string) =>
    api.put<{ open_share_disclosure: string }>(withAccount("/onboarding/open-share-disclosure", requireAccount(accountLogin, "onboardingApi.updateOpenShareDisclosure")), { disclosure }),
  // updateLocale sets the galaxy display language ('ja'|'en'; admin-only). Drives
  // channel-wide localized output like the Slack digest.
  updateLocale: (accountLogin: string, locale: string) =>
    api.put<{ locale: string }>(withAccount("/onboarding/locale", requireAccount(accountLogin, "onboardingApi.updateLocale")), { locale }),
  members: (accountLogin: string) =>
    api.get<AccountMembership[]>(withAccount("/onboarding/members", requireAccount(accountLogin, "onboardingApi.members"))),
};

export interface AccountMembership {
  id: number;
  account_id: number;
  user_id: number;
  role: string;
  created_at: string;
}

// Invitations
export interface Invitation {
  id: number;
  account_id: number;
  invite_code: string;
  role: string;
  status: string;
  invited_by: number;
  expires_at: string;
  created_at: string;
  // Targeted invites are bound to one observed github author: only that person
  // (matching github_user_id at accept time) can claim the link. Absent on a
  // generic invite, which anyone may accept.
  target_github_user_id?: number;
  target_github_login?: string;
}

// CreateInviteOptions binds an invite to a specific observed author when target
// fields are passed; omit them for a generic, anyone-can-accept link.
export interface CreateInviteOptions {
  role?: string;
  expiresInDays?: number;
  targetGitHubUserID?: number;
  targetGitHubLogin?: string;
}

export const invitationApi = {
  list: (accountLogin: string) =>
    api.get<Invitation[]>(withAccount("/invitations", requireAccount(accountLogin, "invitationApi.list"))),
  create: (accountLogin: string, opts: CreateInviteOptions = {}) =>
    api.post<Invitation>(withAccount("/invitations", requireAccount(accountLogin, "invitationApi.create")), {
      role: opts.role || "member",
      expires_in_days: opts.expiresInDays || 7,
      target_github_user_id: opts.targetGitHubUserID,
      target_github_login: opts.targetGitHubLogin,
    }),
  // accept binds the JWT user to the invite's account — no current-galaxy
  // needed (the invite carries its own target account).
  accept: (code: string) =>
    api.post<Invitation>("/invitations/accept", { code }),
  // revoke cancels a pending invite link (status → revoked, not a physical
  // delete). Galaxy-scoped server-side.
  revoke: (accountLogin: string, id: number) =>
    api.delete<{ status: string }>(withAccount(`/invitations/${id}`, requireAccount(accountLogin, "invitationApi.revoke"))),
};

// Observed authors — git authors EIS has observed in the galaxy who have no
// OrbitLens account membership yet. Identity only (login + github_user_id),
// never an EIS score (evaluation firewall). The source list for minting a
// person-bound targeted invite. Admin-only, galaxy-scoped server-side.
export interface ObservedAuthor {
  github_user_id: number;
  login: string;
}

export const observedAuthorsApi = {
  list: (accountLogin: string) =>
    api.get<ObservedAuthor[]>(
      withAccount("/observed-authors", requireAccount(accountLogin, "observedAuthorsApi.list")),
    ),
};
